import { useMemo, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { format, formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';
import {
  ArrowSquareOutIcon,
  ClockCounterClockwiseIcon,
  DownloadSimpleIcon,
  HandPointingIcon,
} from '@phosphor-icons/react';
import type { ActionRun } from '@/api/action-runs-service';
import { actionRunsFeedOptions } from '@/hooks/query-options';
import { PageContent } from '@/components/page-content';
import { DataTable } from '@/components/ui/data-table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { actionVisual, ACCENT_TILE } from '@/components/actions/action-visuals';
import {
  RUN_STATUS_VARIANT,
  RUN_TRIGGER_META,
  runDurationLabel,
} from '@/components/actions/run-meta';
import { downloadCsv } from '@/lib/csv';
import { toTitleCase } from '@/utils/to-title-case';
import { cn } from '@/utils/cn';

export const Route = createFileRoute(
  '/_auth/_side-layout/actions/your-actions/activity',
)({
  component: ActivityPage,
  loader: ({ context: { queryClient } }) =>
    queryClient.ensureInfiniteQueryData(actionRunsFeedOptions()),
});

// The run history / audit trail for every action (design 6d): Time / Action /
// Trigger / Result / Duration / Output, with search + result + date filters
// and a CSV export of the visible slice.
function buildColumns(
  onView: (run: ActionRun) => void,
): ColumnDef<ActionRun>[] {
  return [
    {
      id: 'time',
      header: 'Time',
      cell: ({ row }) => (
        <span
          className="text-sm whitespace-nowrap"
          title={formatDistanceToNow(new Date(row.original.started_at), {
            addSuffix: true,
          })}
        >
          {format(new Date(row.original.started_at), 'MMM d, h:mm a')}
        </span>
      ),
    },
    {
      id: 'action',
      header: 'Action',
      cell: ({ row }) => {
        const { Icon, accent } = actionVisual(row.original.action_key);
        return (
          <div className="flex min-w-0 items-center gap-2.5">
            <span
              className={cn(
                'flex size-7 shrink-0 items-center justify-center rounded-md ring-1 ring-inset [&_svg]:size-3.5',
                ACCENT_TILE[accent],
              )}
            >
              <Icon />
            </span>
            <div className="flex min-w-0 flex-col">
              <span className="truncate text-sm font-medium">
                {row.original.action_name ??
                  row.original.action_label}
              </span>
              {row.original.action_name && (
                <span className="text-muted-foreground truncate text-xs">
                  {row.original.action_label}
                </span>
              )}
            </div>
          </div>
        );
      },
    },
    {
      id: 'trigger',
      header: 'Trigger',
      cell: ({ row }) => {
        const meta = RUN_TRIGGER_META[row.original.trigger_source] ?? {
          icon: HandPointingIcon,
          label: 'Manual',
        };
        const TriggerIcon = meta.icon;
        return (
          <div className="text-muted-foreground flex items-center gap-1.5 text-sm">
            <TriggerIcon className="size-4 shrink-0" />
            {meta.label}
          </div>
        );
      },
    },
    {
      id: 'result',
      header: 'Result',
      cell: ({ row }) => (
        <Badge size="sm" variant={RUN_STATUS_VARIANT[row.original.status]}>
          {toTitleCase(row.original.status)}
        </Badge>
      ),
    },
    {
      id: 'duration',
      header: 'Duration',
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm whitespace-nowrap">
          {runDurationLabel(row.original)}
        </span>
      ),
    },
    {
      id: 'output',
      header: () => <span className="sr-only">Output</span>,
      cell: ({ row }) => (
        <div className="flex justify-end">
          <Button
            variant="ghost"
            size="icon"
            aria-label="View output"
            onClick={() => onView(row.original)}
          >
            <ArrowSquareOutIcon className="size-4" />
          </Button>
        </div>
      ),
    },
  ];
}

// Export the FILTERED rows as CSV (client-side; the visible slice IS the
// audit view the user is looking at).
function exportCsv(runs: ActionRun[]) {
  downloadCsv(
    'action-runs',
    ['time', 'action', 'name', 'trigger', 'result', 'duration', 'error'],
    runs.map((run) => [
      run.started_at,
      run.action_label,
      run.action_name,
      run.trigger_source,
      run.status,
      runDurationLabel(run),
      run.error,
    ]),
  );
}

const DATE_RANGES = [
  { value: '7', label: 'Last 7 Days' },
  { value: '30', label: 'Last 30 Days' },
  { value: '90', label: 'Last 90 Days' },
  { value: 'all', label: 'All Time' },
] as const;

type DateRange = (typeof DATE_RANGES)[number]['value'];
type ResultFilter = 'all' | ActionRun['status'];

// The search / result / date-range predicate, shared by the on-screen table and
// the CSV export so both slice the run set identically.
function filterRuns(
  runs: ActionRun[],
  search: string,
  result: ResultFilter,
  range: DateRange,
): ActionRun[] {
  const term = search.trim().toLowerCase();
  const since = range === 'all' ? 0 : Date.now() - Number(range) * 86_400_000;
  return runs.filter((run) => {
    if (result !== 'all' && run.status !== result) return false;
    if (since && new Date(run.started_at).getTime() < since) return false;
    if (!term) return true;
    return (
      run.action_label.toLowerCase().includes(term) ||
      (run.action_name?.toLowerCase().includes(term) ?? false)
    );
  });
}

function ActivityTable() {
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery(actionRunsFeedOptions());
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [result, setResult] = useState<ResultFilter>('all');
  const [range, setRange] = useState<DateRange>('30');
  const [isExporting, setIsExporting] = useState(false);

  const runs = useMemo(
    () => (data?.pages ?? []).flatMap((page) => page.runs),
    [data],
  );
  const filtered = useMemo(
    () => filterRuns(runs, search, result, range),
    [runs, search, result, range],
  );

  // CSV export covers the WHOLE history, not just the loaded slice: pull every
  // remaining page first, then apply the same filter before writing the file.
  const handleExport = async () => {
    setIsExporting(true);
    try {
      let pages = data?.pages ?? [];
      if (hasNextPage) {
        let page = await fetchNextPage({ throwOnError: true });
        while (page.hasNextPage) {
          page = await fetchNextPage({ throwOnError: true });
        }
        pages = page.data?.pages ?? pages;
      }
      const allRuns = pages.flatMap((page) => page.runs);
      exportCsv(filterRuns(allRuns, search, result, range));
    } catch {
      toast.error('Could not export the activity log. Try again.');
    } finally {
      setIsExporting(false);
    }
  };

  // Results open as a full page (like Company Profiles), not a modal.
  const openRun = (run: ActionRun) =>
    navigate({
      to: '/actions/your-actions/activity/$runId',
      params: { runId: run.id },
    });
  const columns = useMemo(() => buildColumns(openRun), []);

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search runs…"
          className="sm:max-w-xs"
        />
        <Select
          value={result}
          onValueChange={(v) => setResult(v as ResultFilter)}
        >
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Results</SelectItem>
            <SelectItem value="succeeded">Succeeded</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
            <SelectItem value="running">Running</SelectItem>
          </SelectContent>
        </Select>
        <Select value={range} onValueChange={(v) => setRange(v as DateRange)}>
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {DATE_RANGES.map((r) => (
              <SelectItem key={r.value} value={r.value}>
                {r.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex-1" />
        <Button
          variant="outline"
          iconLeft={<DownloadSimpleIcon />}
          onClick={handleExport}
          loading={isExporting}
          // Export pulls ALL pages then filters, so enable while more pages may
          // still match even if none in the loaded slice do.
          disabled={(filtered.length === 0 && !hasNextPage) || isExporting}
        >
          Export CSV
        </Button>
      </div>

      <DataTable
        columns={columns}
        data={filtered}
        // Paging is server-driven via the "Load More" button below — render every
        // loaded row and suppress DataTable's own client pager (otherwise both
        // controls show at once and "Load More" snaps the client table to page 1).
        manualPagination
        isLoading={isLoading}
        onRowClick={openRun}
        emptyMessage="No Runs Yet"
        emptyDescription="Every run across your actions lands here."
        emptyIcon={ClockCounterClockwiseIcon}
      />

      {hasNextPage && (
        <div className="flex justify-center pt-2">
          <Button
            variant="outline"
            loading={isFetchingNextPage}
            onClick={() => fetchNextPage()}
          >
            Load More
          </Button>
        </div>
      )}
    </div>
  );
}

function ActivityPage() {
  return (
    <PageContent>
      <ActivityTable />
    </PageContent>
  );
}
